<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import ExportDataButton from '@/components/ExportDataButton.vue';
import PageState from '@/components/PageState.vue';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';

interface AuditChange {
  field: string;
  label: string;
  /** 没收录的系统内部字段，界面标灰 */
  internal?: boolean;
  before: string | null;
  after: string | null;
}

interface AuditEntry {
  id: number;
  moduleKey: string;
  entityType: string;
  entityId: string;
  action: string;
  actionLabel: string;
  actorName: string | null;
  createdAt: string;
  subject: string | null;
  /** 属于哪个项目（0825）；不属于项目的记录为 null */
  projectName?: string | null;
  /** 同一次请求写的几条共一个 id（1006）；没有就 null */
  requestId?: string | null;
  changes: AuditChange[];
}

interface Facet { value: string; label: string; count: number }

const PAGE_SIZE = 50;

const entries = ref<AuditEntry[]>([]);

/**
 * 按「一次操作」折叠（1006）：同一个 request id 连着的几条收成一组。
 * 一次点击批量改 167 个节点，原来留痕页就是 167 行一样的「更新计划节点」，翻都翻不完。
 * 一组默认只露头一条和「共 N 条」，点开再看全部。request id 为空的（定时任务、老数据）照旧一行一条。
 */
interface EntryGroup { key: string; head: AuditEntry; items: AuditEntry[] }
const expandedGroups = ref<Set<string>>(new Set());
const groups = computed<EntryGroup[]>(() => {
  const out: EntryGroup[] = [];
  for (const entry of entries.value) {
    const last = out[out.length - 1];
    if (entry.requestId && last && last.head.requestId === entry.requestId) {
      last.items.push(entry);
      continue;
    }
    out.push({ key: entry.requestId ? `req:${entry.requestId}` : `id:${entry.id}`, head: entry, items: [entry] });
  }
  return out;
});
function toggleGroup(key: string): void {
  const next = new Set(expandedGroups.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  expandedGroups.value = next;
}
/** 一组里都是同一种动作就说「批量 X」，混着的说「一次操作」 */
function groupTitle(group: EntryGroup): string {
  const actions = new Set(group.items.map((item) => entryActionText(item)));
  const types = new Set(group.items.map((item) => entityLabel(item.entityType)));
  const what = actions.size === 1 ? `批量${entryActionText(group.head)}` : '一次操作';
  const target = types.size === 1 ? [...types][0] : '多种记录';
  return `${what} · ${group.items.length} 条${target}`;
}
const total = ref(0);
const offset = ref(0);
const loading = ref(true);
const error = ref('');
const facets = ref<{ modules: Facet[]; entityTypes: Facet[]; actions: Facet[] }>({
  modules: [], entityTypes: [], actions: [],
});

const moduleKey = ref('');
const entityType = ref('');
const action = ref('');
const actor = ref('');
const entityId = ref('');
const from = ref('');
const to = ref('');

const ENTITY_LABELS: Record<string, string> = {
  'plan-node': '计划节点',
  project: '项目',
  deliverable: '交付物',
  'project-member': '项目成员',
  document: '文件',
  'document-version': '文件版本',
  'pfmea-item': 'PFMEA 条目',
  issue: '问题',
  iam_user: '用户',
  // 0824 补：线上真实出现过、但这张表里没有的（Tony「太硬核了」）
  'plan-node-review': '节点评审',
  // 0909：责任人重新提交时作废的那一整轮评审结论，原样存在这里
  'plan-node-review-round': '节点评审（作废的一轮）',
  'pfmea-document': 'PFMEA 文件',
  'change-action': '变更措施',
  change: '工程变更',
  bom: 'BOM',
  'bom-item': 'BOM 行',
  'quality-document': '质量文件',
  milestone: '里程碑',
  'approval-workflow': '审批流',
  'change-task': '变更任务',
  // 1027 复评补：留痕下拉里真实出现的对象码
  meeting: '会议',
  task: '任务',
  inspection: '点检',
  mould: '模具',
  activity: '活动',
  escalation: '升级问题',
  approval: '审批',
  'dfmea-document': 'DFMEA 文件',
  'dfmea-item': 'DFMEA 条目',
  user: '用户',
  // 1027 复评 r2：照后端写留痕的地方逐个对过，写得进来、表里却没有的对象码
  tl_moulds: '模具',
  'work-item': '任务事项',
  delegation: '派活',
  'project-file': '项目文件',
  'record-attachment': '业务附件',
  'ppap-package': 'PPAP 包',
  'ppap-element': 'PPAP 要素',
  mbom: 'MBOM',
  'directory-sync': '人员同步',
  'project-facts': '项目指标',
  'meeting-series': '会议序列',
  'dimensional-report': '全尺寸报告',
  'drawing-characteristic': '图纸特性',
  'control-plan': '控制计划',
  'control-plan-row': '控制计划行',
  'dfmea-action': 'DFMEA 措施',
  '8d-step': '8D 步骤',
  'org-unit': '组织',
  dictionary: '字典',
  template: '模板',
  'config-release': '配置发布',
  notification: '通知',
  /*
    1028 Soft-Gate P2（复评步骤 23）：对象下拉里还剩这两个英文码。
    drawing 是项目图纸（drawings.service 建 / 删 / 重编号时写）；
    role-name 在 10-27 的后端快照里没找到写入点，按审批流 role_names（角色名）的口径翻。
  */
  drawing: '图纸',
  'role-name': '角色名',
};

const MODULE_LABELS: Record<string, string> = {
  'project-management': '项目管理',
  documents: '文件管理',
  'pfmea-cp': 'PFMEA / 控制计划',
  issues: '问题管理',
  hr: '人力资源',
  'admin-users': '用户管理',
  // 1027 复评补：下拉里 inspection / changes / tasks / meetings 原来全显示成同一句「待确认类型」
  inspection: '点检',
  changes: '变更管理',
  tasks: '任务',
  meetings: '会议',
  dfmea: 'DFMEA',
  moulds: '模具',
  escalations: '升级问题',
  approvals: '审批',
  director: '总监看板',
  portfolio: '项目组合',
  'my-work': '我的工作',
  nodes: '节点',
  bigscreen: '大屏',
  account: '账号',
  // 1027 复评 r2 点名：这四个还显示「未登记模块（码）」
  tooling: '模具开发',
  projects: '项目工作区',
  dashboard: '指挥台 / 业务指标',
  notifications: '通知',
  // 同批补上后端其余会写留痕的模块，别等下一轮复评再点名
  workbench: '工作台',
  calendar: '日历',
  ai: 'AI 助手',
  auth: '登录认证',
  'config-view': '系统配置',
  'mail-inbound': '邮件收件',
  settings: '系统设置',
  deliverables: '交付物',
};

/*
  表里对不上的写「未登记模块（码）」/「未登记对象（码）」（1027 复评）。
  以前统一兜成「待确认类型」，下拉里四项同名、根本分不出选的是哪个；
  模块和对象也不共用一句兜底。筛选请求照旧传原始 value。
*/
function entityLabel(value: string): string {
  if (!value) return '—';
  return ENTITY_LABELS[value] ?? `未登记对象（${value}）`;
}

function moduleLabel(value: string): string {
  if (!value) return '—';
  return MODULE_LABELS[value] ?? `未登记模块（${value}）`;
}

function stamp(value: string): string {
  return value.replace('T', ' ').slice(0, 19);
}

const hasFilters = computed(() => Boolean(
  moduleKey.value || entityType.value || action.value || actor.value
  || entityId.value || from.value || to.value,
));

/** 当前生效的筛选条件。列表和筛选项的条数用**同一份**——两边各组一次迟早对不上 */
function currentFilters(): Record<string, string | undefined> {
  return {
    moduleKey: moduleKey.value || undefined,
    entityType: entityType.value || undefined,
    action: action.value || undefined,
    actor: actor.value.trim() || undefined,
    entityId: entityId.value.trim() || undefined,
    from: from.value || undefined,
    to: to.value || undefined,
  };
}

/** facets 没给中文时的前端兜底，只收常见动作码 */
const ACTION_LABELS: Record<string, string> = {
  create: '创建',
  update: '更新',
  delete: '删除',
  login: '登录',
  logout: '退出',
  view: '查看',
  export: '导出',
  approve: '批准',
  reject: '退回',
  submit: '提交',
  assign: '指派',
  /*
    1027 复评 r2：后端写进留痕、后端中文表却没收的动作码，照写入点逐个补。
    后端表里有的仍以后端为准（facets / actionLabel 给了中文就用它）。
  */
  'tsk.reschedule': '任务改期',
  'tsk.note': '补充任务记录',
  'tsk.hold.request': '申请搁置任务',
  'tsk.hold.approve': '同意搁置',
  'tsk.hold.reject': '驳回搁置',
  'tsk.delegation.edit': '修改派活',
  'mtg.outcome.extract': '解析会议记录',
  'mould.current_state_note': '更新模具当前状态',
  'mould.next_trial_plan_date': '更新下次试模日期',
  'mbom.regenerate.snapshot': '重新生成 MBOM（留快照）',
  milestone_recovery: '里程碑挽回计划',
  plan_relaid: '重新铺设计划',
  'team-autofill': '自动补齐项目团队',
  'owner-autofill': '自动补齐责任人',
  'managers-sync': '同步项目经理',
  'update-helpers': '调整协办人',
  'approval-reassign': '改派审批人',
  'team-handoff-reassign': '团队交接改派',
  resubmit_after_rejection: '退回后重新提交',
  change_closed: '关闭变更',
  change_closed_early: '中途关闭变更',
  reattached_to_rebuilt_project: '挂到重建后的项目',
  escalated: '自动升级',
  sync_completed: '人员同步完成',
  sync_failed: '人员同步失败',
  mark_read: '标记已读',
  restore: '恢复',
  archive: '归档',
  import: '导入',
  upload: '上传',
  download: '下载',
  cancel: '取消',
  close: '关闭',
  reopen: '重新打开',
  remind: '催办',
  /*
    1028 Soft-Gate P2（复评步骤 23）：动作下拉里还剩的英文码。
    retire 是审批流「停用但留着历史」（approval-workflow.service），
    后端表里 deactivate 已叫「停用」，这里写「退役」，免得下拉里两项同名分不出。
  */
  approval_rejected: '审批退回',
  retire: '退役',
  owner_reassigned: '改派负责人',
  /*
    1028 Soft-Gate copy-tail（复评 r2 步骤 9/15）：低频动作下拉里还剩的 11 个英文码，10-27 后端快照的中文表都没收。
    原码照旧留在下拉项和留痕条目的悬停（title）里。
  */
  'mbom.ai.review': 'MBOM AI 复核',
  move: '移动',
  reparent: '改父项',
  released_cross_project_document: '跨项目发布文档',
  'mbom.ai.pairing.progress': 'MBOM AI 配对进度',
  'role-rename': '角色改名',
  'mbom.ai.blank': 'MBOM AI 留白',
  'follow-dingtalk': '钉钉跟进',
  de_escalated: '降级',
  'retire-remap': '退役重映射',
  'mbom.ai.blank.decision': 'MBOM AI 留白决策',
};

const CHINESE = /[一-鿿]/;

/**
 * 动作码的中文：facets（后端那张表）→ 前端兜底表 →「动作（码）」，不原样丢英文。
 * facets 的 label 要**是中文**才用：后端表里没有的码，它给的 label 就是英文码本身，
 * 1027 r2 之前拿它直接当中文用，前端兜底表一次都轮不到。
 */
function actionText(value: string): string {
  if (!value) return '—';
  const fromFacets = facets.value.actions.find((item) => item.value === value)?.label?.trim();
  if (fromFacets && CHINESE.test(fromFacets)) return fromFacets;
  return ACTION_LABELS[value] ?? `动作（${value}）`;
}

/** 列表 / 分组 / 导出展示的动作（1027 复评）：actionLabel 已是中文就用它，英文码走 actionText 兜底 */
function entryActionText(entry: AuditEntry): string {
  const label = entry.actionLabel?.trim();
  if (label && CHINESE.test(label)) return label;
  if (entry.action) return actionText(entry.action);
  return label || '—';
}

/** 空结果时，把当前生效的条件摆出来——人才知道是哪一条把结果筛没了 */
const activeFilterText = computed(() => {
  const parts: string[] = [];
  if (moduleKey.value) parts.push(`模块「${moduleLabel(moduleKey.value)}」`);
  if (entityType.value) parts.push(`对象类型「${entityLabel(entityType.value)}」`);
  if (action.value) parts.push(`动作「${actionText(action.value)}」`);
  if (actor.value.trim()) parts.push(`操作人「${actor.value.trim()}」`);
  if (entityId.value.trim()) parts.push(`对象 ID「${entityId.value.trim()}」`);
  if (from.value || to.value) parts.push(`时间 ${from.value || '不限'} ~ ${to.value || '不限'}`);
  return parts;
});

async function loadFacets(): Promise<void> {
  try {
    const payload = await eqsApi.auditFacets(currentFilters());
    facets.value = payload as unknown as typeof facets.value;
  } catch {
    // 筛选项取不到不影响查询本身
  }
}

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.auditEvents({
      ...currentFilters(),
      limit: PAGE_SIZE,
      offset: offset.value,
    });
    entries.value = (payload.entries ?? []) as unknown as AuditEntry[];
    total.value = Number(payload.total ?? 0);
    // 条数跟着条件走：选了操作人之后，下拉里那些他没碰过的类型就该显示 (0)
    void loadFacets();
  } catch (caught) {
    entries.value = [];
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

async function search(): Promise<void> {
  offset.value = 0;
  await load();
}

/** 手动改筛选条件后再查：概览带进来的那句回显就不成立了，得撤掉 */
async function manualSearch(): Promise<void> {
  activeFilterNote.value = '';
  await search();
}

function clearFilters(): void {
  activeFilterNote.value = '';
  moduleKey.value = '';
  entityType.value = '';
  action.value = '';
  actor.value = '';
  entityId.value = '';
  from.value = '';
  to.value = '';
  void search();
}

async function page(delta: number): Promise<void> {
  offset.value = Math.max(0, offset.value + delta * PAGE_SIZE);
  await load();
}

/*
  导出（0824 Tony：审计 / 合规要用，得能导出给人看）。

  **导的是筛选后的全部，不是当前这 50 条**：审计要的是"这段时间这个人做过什么"
  的完整记录，导出一页给人，等于给了一份看着像全量、其实缺了大半的证据。
  所以这里重新拉一次、按 total 取；上限 5000 条兜底，
  超了就照实说，不静默截断（截断的审计报告比没有更危险）。

  一条改动摊成一行（字段/改前/改后各一列）而不是一条一行：
  Excel 里一条记录挤着五个字段的变化，没法筛也没法排。
  没有字段级差异的（新建、状态推进）仍然占一行，动作和对象要在。
*/
/*
  概览（0824 Tony「聚合视图也做一下」）。

  原来这页只有一排筛选器，**要求人先知道模块 / 类型 / 对象 ID 才查得动**。
  但 Tony 说的用途里有一个是「定期翻一翻，看团队在系统里真用了没有」——
  那是统计问题，筛选器回答不了。所以在筛选区上面摆三块：
  谁在动、哪几条被动得最多、每天多少条。

  三块都**可点**：点操作人就筛他的，点热点对象就筛那一条——
  概览不是只能看的装饰，它是筛选器的另一种入口。
*/
interface SummaryActor { actorName: string | null; total: number; lastAt: string; daysActive: number }
interface SummaryDaily { day: string; total: number; actors: number }
interface SummaryHot {
  entityType: string; entityId: string; subject: string | null;
  total: number; actors: number; lastAt: string;
}

const summary = ref<{ days: number; actors: SummaryActor[]; daily: SummaryDaily[]; hot: SummaryHot[] } | null>(null);
const summaryOpen = ref(true);
const summaryDays = ref(30);

async function loadSummary(): Promise<void> {
  try {
    summary.value = await eqsApi.auditSummary(summaryDays.value) as unknown as typeof summary.value;
  } catch {
    // 概览挂了不该挡着查日志——这页的主功能是检索，概览是附加的
    summary.value = null;
  }
}

/** 每天多少条，画成一排小柱子；最高的那天占满，其余按比例 */
const dailyMax = computed(() => Math.max(1, ...(summary.value?.daily ?? []).map((d) => d.total)));

/*
  点概览 = 下筛选（0825 Tony：「被改的最多的记录点击没反应」）。

  逻辑本来就是通的——实测点一下确实带着 entityType/entityId 重新查了。
  问题出在**看不见结果**：概览占了大半屏，结果列表在它下面，
  点完之后视线还停在概览上，而唯一的反馈是筛选区里多出来的一个「清除」按钮。
  功能对、人以为没反应，这和坏了没区别。

  所以点完做三件事：把条件回显在结果区抬头、滚过去、让列表闪一下。
*/
const activeFilterNote = ref('');
const listAnchor = ref<HTMLElement | null>(null);

async function applyFilter(note: string): Promise<void> {
  activeFilterNote.value = note;
  await search();
  // 滚到结果区：点了就该看见结果，而不是让人自己往下找
  // （scrollIntoView 也要 ?. —— jsdom 里没有这个方法，单测会炸成 unhandled rejection）
  await nextTick();
  listAnchor.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
}

/** 点操作人 = 按他筛。概览要能当入口用，不然就只是个摆设 */
async function filterByActor(name: string | null): Promise<void> {
  if (!name) return;
  actor.value = name;
  await applyFilter(`只看「${name}」的操作`);
}

async function filterByHot(item: SummaryHot): Promise<void> {
  entityType.value = item.entityType;
  entityId.value = item.entityId;
  const label = item.subject || `${entityLabel(item.entityType)} #${item.entityId}`;
  await applyFilter(`只看「${label}」这一条记录的改动`);
}

/** 「近 N 天」标签：日期本身也要人话，2026-08-24 看不出是不是今天 */
function dayLabel(day: string): string {
  const today = new Date().toISOString().slice(0, 10);
  if (day === today) return '今天';
  return day.slice(5);
}

const EXPORT_CAP = 5000;
const exporting = ref(false);
const exportRows = ref<Array<Record<string, string>>>([]);

const EXPORT_COLUMNS = [
  { key: 'time', label: '时间' },
  { key: 'actor', label: '操作人' },
  { key: 'action', label: '动作' },
  { key: 'module', label: '模块' },
  { key: 'entity', label: '对象类型' },
  { key: 'project', label: '所属项目' },
  { key: 'subject', label: '对象' },
  { key: 'entityId', label: '对象 ID' },
  { key: 'field', label: '字段' },
  { key: 'before', label: '改前' },
  { key: 'after', label: '改后' },
];

async function buildExport(): Promise<void> {
  if (exporting.value) return;
  exporting.value = true;
  try {
    const payload = await eqsApi.auditEvents({
      moduleKey: moduleKey.value || undefined,
      entityType: entityType.value || undefined,
      action: action.value || undefined,
      actor: actor.value.trim() || undefined,
      entityId: entityId.value.trim() || undefined,
      from: from.value || undefined,
      to: to.value || undefined,
      limit: Math.min(total.value || PAGE_SIZE, EXPORT_CAP),
      offset: 0,
    });
    const all = (payload.entries ?? []) as unknown as AuditEntry[];
    const rows: Array<Record<string, string>> = [];
    for (const entry of all) {
      const base = {
        time: stamp(entry.createdAt),
        actor: entry.actorName || '未知操作人',
        action: entryActionText(entry),
        module: moduleLabel(entry.moduleKey),
        entity: entityLabel(entry.entityType),
        project: entry.projectName || '',
        subject: entry.subject || '',
        entityId: entry.entityId,
      };
      if (!entry.changes.length) {
        rows.push({ ...base, field: '', before: '', after: '' });
        continue;
      }
      for (const change of entry.changes) {
        rows.push({
          ...base,
          field: change.label,
          before: change.before ?? '（空）',
          after: change.after ?? '（空）',
        });
      }
    }
    exportRows.value = rows;
    if (total.value > EXPORT_CAP) {
      notify('info', `只导出了最近 ${EXPORT_CAP} 条`,
        `当前筛选命中 ${total.value} 条，超过单次上限。缩小时间范围再导一次可以拿到全部。`);
    }
  } catch (caught) {
    exportRows.value = [];
    notify('error', '导出准备失败', errorMessage(caught));
  } finally {
    exporting.value = false;
  }
}

onMounted(async () => {
  await Promise.all([loadFacets(), load(), loadSummary()]);
});
</script>

<template>
  <header class="subpage-context">
    <p>
      <span>AUDIT TRAIL</span>
      系统一直在记录谁在什么时候改了什么。这里可以查回去——比如「这条计划日期上周是几号」。
    </p>
  </header>

  <!--
    概览（0824）。摆在筛选器**之前**：原来打开这页第一眼是一排要求填写的下拉框，
    人得先知道自己要查什么才用得动。现在第一眼是「最近谁在动、哪几条被动得最多」，
    看完再决定往哪筛——每一块都能点，点了就是筛选条件。
  -->
  <section v-if="summary" class="audit-summary" aria-label="审计概览">
    <header>
      <b>最近 {{ summary.days }} 天</b>
      <select v-model.number="summaryDays" @change="loadSummary">
        <option :value="7">近 7 天</option>
        <option :value="30">近 30 天</option>
        <option :value="90">近 90 天</option>
      </select>
      <button type="button" class="inline-button" @click="summaryOpen = !summaryOpen">
        {{ summaryOpen ? '收起概览' : '展开概览' }}
      </button>
    </header>

    <div v-if="summaryOpen" class="audit-summary-body">
      <div class="audit-card">
        <b>谁在动</b>
        <p v-if="!summary.actors.length" class="audit-card-empty">这段时间没有人操作。</p>
        <ul v-else>
          <li v-for="item in summary.actors.slice(0, 8)" :key="item.actorName ?? '?'">
            <button
              type="button" class="audit-rank"
              :title="`按「${item.actorName || '未知操作人'}」筛选`"
              @click="filterByActor(item.actorName)"
            >{{ item.actorName || '未知操作人' }}</button>
            <span>{{ item.total }} 次 · {{ item.daysActive }} 天</span>
          </li>
        </ul>
      </div>

      <div class="audit-card">
        <b>被改得最多的记录</b>
        <p class="audit-card-hint">同一条反复改，通常就是出过问题的那条</p>
        <p v-if="!summary.hot.length" class="audit-card-empty">这段时间没有记录被改过两次以上。</p>
        <ul v-else>
          <li v-for="item in summary.hot.slice(0, 8)" :key="`${item.entityType}:${item.entityId}`">
            <button
              type="button" class="audit-rank"
              :title="`${item.subject || entityLabel(item.entityType) + ' #' + item.entityId} —— 点这里只看它的改动`"
              @click="filterByHot(item)"
            >{{ item.subject || `${entityLabel(item.entityType)} #${item.entityId}` }}</button>
            <span>{{ item.total }} 次 · {{ item.actors }} 人</span>
          </li>
        </ul>
      </div>

      <div class="audit-card audit-daily">
        <b>每天多少条</b>
        <p v-if="!summary.daily.length" class="audit-card-empty">这段时间没有操作记录。</p>
        <ol v-else class="audit-bars">
          <!-- 倒序拿到的是最近在前，画图要最近在右，反过来 -->
          <li v-for="day in [...summary.daily].reverse()" :key="day.day" :title="`${day.day}：${day.total} 条 · ${day.actors} 人`">
            <i :style="{ height: `${Math.round((day.total / dailyMax) * 100)}%` }"></i>
            <em>{{ dayLabel(day.day) }}</em>
          </li>
        </ol>
      </div>
    </div>
  </section>

  <section class="audit-filters" aria-label="审计筛选">
    <label>
      <span>模块</span>
      <select v-model="moduleKey">
        <option value="">全部模块</option>
        <option v-for="item in facets.modules" :key="item.value" :value="item.value">
          {{ moduleLabel(item.value) }}（{{ item.count }}）
        </option>
      </select>
    </label>
    <label>
      <span>对象类型</span>
      <select v-model="entityType">
        <option value="">全部类型</option>
        <!-- 原始码只放悬停（1028 Soft-Gate P2）：下拉正文只写中文，查库对码时再看 title -->
        <option v-for="item in facets.entityTypes" :key="item.value" :value="item.value" :title="item.value">
          {{ entityLabel(item.value) }}（{{ item.count }}）
        </option>
      </select>
    </label>
    <label>
      <span>动作</span>
      <select v-model="action">
        <option value="">全部动作</option>
        <option v-for="item in facets.actions" :key="item.value" :value="item.value" :title="item.value">
          {{ actionText(item.value) }}（{{ item.count }}）
        </option>
      </select>
    </label>
    <label>
      <span>操作人</span>
      <input v-model="actor" type="search" placeholder="姓名片段即可">
    </label>
    <label>
      <span>对象 ID</span>
      <input v-model="entityId" type="search" placeholder="如 3074">
    </label>
    <label>
      <span>起</span>
      <input v-model="from" type="date">
    </label>
    <label>
      <span>止</span>
      <input v-model="to" type="date">
    </label>
    <div class="audit-actions">
      <button class="button button-primary" type="button" @click="manualSearch">查询</button>
      <button v-if="hasFilters" class="button" type="button" @click="clearFilters">清除</button>
    </div>
  </section>

  <PageState v-if="loading" state="loading" title="正在读取审计记录" message="按时间倒序加载。" />
  <PageState
    v-else-if="error"
    state="error"
    title="审计记录不可用"
    message="该页面仅管理员可见。"
    :detail="error"
    @retry="load"
  />
  <template v-else>
    <!--
      从概览点进来时，在结果区抬头说清楚"现在只看什么"（0825）。
      光靠筛选区多出来一个「清除」按钮，人根本注意不到，
      于是"点了确实查了"和"点了没反应"在他眼里长得一模一样。
    -->
    <p v-if="activeFilterNote" ref="listAnchor" class="audit-active-filter">
      <b>{{ activeFilterNote }}</b>
      <span>共 {{ total }} 条</span>
      <button type="button" class="inline-button" @click="clearFilters">看全部 ↺</button>
    </p>
    <div class="audit-count">
      <p>共 {{ total }} 条，当前显示第 {{ total ? offset + 1 : 0 }}–{{ offset + entries.length }} 条</p>
      <!--
        导出（0824 Tony：审计 / 合规要用）。两步走：先按当前筛选把全量拉回来
        （不是这一页），再交给通用导出按钮出 CSV。
        分两步是因为 ExportDataButton 只认现成的 rows——
        让它自己去拉数据，等于把"导全量还是导一页"的判断散到组件里。
      -->
      <span class="audit-export">
        <button class="button button-secondary" type="button" :disabled="exporting || !total" @click="buildExport">
          {{ exporting ? '准备中…' : `准备导出（${total} 条）` }}
        </button>
        <ExportDataButton
          v-if="exportRows.length"
          :columns="EXPORT_COLUMNS"
          :rows="exportRows"
          filename="操作留痕"
          :label="`下载 CSV（${exportRows.length} 行）`"
        />
      </span>
    </div>

    <div v-if="entries.length" class="audit-list">
      <template v-for="group in groups" :key="group.key">
      <!-- 一次操作改了多条：折成一组，露头一条 + 共几条，点开再看全部（1006） -->
      <article v-if="group.items.length > 1" class="audit-group" :class="{ 'is-open': expandedGroups.has(group.key) }">
        <header>
          <b>{{ groupTitle(group) }}</b>
          <span v-if="group.head.projectName" class="audit-project">{{ group.head.projectName }}</span>
          <span class="audit-meta">{{ group.head.actorName || '未知操作人' }} · {{ stamp(group.head.createdAt) }}</span>
          <button type="button" class="inline-button audit-group-toggle" @click="toggleGroup(group.key)">
            {{ expandedGroups.has(group.key) ? '收起' : `展开 ${group.items.length} 条` }}
          </button>
        </header>
        <ul v-if="!expandedGroups.has(group.key)" class="audit-group-peek">
          <li v-for="item in group.items.slice(0, 3)" :key="item.id">
            {{ item.subject || `${entityLabel(item.entityType)} #${item.entityId}` }}
            <template v-if="item.changes.length"> · {{ item.changes.map((change) => change.label).join('、') }}</template>
          </li>
          <li v-if="group.items.length > 3" class="audit-group-more">…等 {{ group.items.length }} 条</li>
        </ul>
      </article>
      <template v-for="entry in (group.items.length === 1 || expandedGroups.has(group.key) ? group.items : [])" :key="entry.id">
      <article :class="{ 'audit-group-member': group.items.length > 1 }">
        <header>
          <!-- 英文原值留在悬停（1028 Soft-Gate P2），正文只写中文 -->
          <b :title="entry.action">{{ entryActionText(entry) }}</b>
          <!--
            认得出对象才谈得上"好找"（0824 Tony）：有名字就把名字放在最显眼的位置，
            #ID 退到后面当补充。以前反过来——一屏「项目 #23」，
            查的人根本不知道说的是哪个项目。
          -->
          <!--
            项目名摆在对象名之前（0825 Tony：「把修改的具体项目名称也写上，
            这样就知道在哪个项目里面更改的了」）——同一个
            「P04-认可工装、设备阶段」在几十个项目里都有一条。
          -->
          <span v-if="entry.projectName" class="audit-project">{{ entry.projectName }}</span>
          <template v-if="entry.subject">
            <span class="audit-subject" :title="entry.subject">{{ entry.subject }}</span>
            <span class="audit-entity" :title="entry.entityType">{{ entityLabel(entry.entityType) }} #{{ entry.entityId }}</span>
          </template>
          <span v-else class="audit-entity" :title="entry.entityType">{{ entityLabel(entry.entityType) }} #{{ entry.entityId }}</span>
          <span class="audit-meta">{{ entry.actorName || '未知操作人' }} · {{ stamp(entry.createdAt) }}</span>
        </header>
        <!--
          改前/改后紧凑并排（1016 Tony：操作留痕页横向溢出）。
          宽屏左右对照，窄屏上下叠；长文案换行 + 行数上限，悬停 title 看全文。
          不再用三列表格——长值会把整页撑出横向滚动条。
        -->
        <ul v-if="entry.changes.length" class="audit-changes">
          <!-- 没收录的系统内部字段标灰：让人一眼知道这行不用管，而不是看不懂在自责 -->
          <li
            v-for="change in entry.changes"
            :key="change.field"
            :class="{ internal: change.internal }"
          >
            <b class="audit-change-field">{{ change.label }}</b>
            <div class="audit-change-pair">
              <div class="audit-change-cell">
                <em>改前</em>
                <span
                  class="audit-before"
                  :title="change.before ?? '（空）'"
                >{{ change.before ?? '（空）' }}</span>
              </div>
              <span class="audit-change-arrow" aria-hidden="true">→</span>
              <div class="audit-change-cell">
                <em>改后</em>
                <span
                  class="audit-after"
                  :title="change.after ?? '（空）'"
                >{{ change.after ?? '（空）' }}</span>
              </div>
            </div>
          </li>
        </ul>
        <p v-else class="audit-nochange">
          {{ entry.action === 'create' ? '新建记录，内容见记录本身。' : '本次没有字段级差异。' }}
        </p>
        <small class="audit-module">{{ moduleLabel(entry.moduleKey) }}</small>
      </article>
      </template>
      </template>
    </div>
    <!--
      空结果要说清楚是**哪些条件**筛没的，不能只丢一句"换个条件试试"。
      0902 Tony 遇到的那次：选了操作人「曾喜信」+ 对象类型 meeting，
      而下拉里还挂着 meeting (28) —— 那 28 条一条都不是他的。
      现在条数跟着条件走会显示 (0)，这里再把生效的条件列出来兜一道。
    -->
    <PageState
      v-else state="empty" title="没有符合条件的审计记录"
      :message="activeFilterText.length
        ? `当前同时满足：${activeFilterText.join(' + ')}。逐个去掉试试——下拉里显示 (0) 的那一项就是筛没结果的那个。`
        : '这个时间范围里还没有记录。'" />

    <div v-if="total > entries.length || offset > 0" class="audit-pager">
      <button class="button" type="button" :disabled="offset === 0" @click="page(-1)">上一页</button>
      <button
        class="button"
        type="button"
        :disabled="offset + entries.length >= total"
        @click="page(1)"
      >下一页</button>
    </div>
  </template>
</template>

<style scoped>
.audit-filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: end;
  margin: 16px 0 12px;
  padding: 12px;
  border: 1px solid var(--line);
  background: var(--white);
}

.audit-filters label { display: grid; gap: 4px; }
.audit-filters label span { color: var(--muted); font-size: 12px; }
.audit-filters select, .audit-filters input { height: 34px; padding: 0 9px; border: 1px solid var(--line); background: var(--white); font: inherit; font-size: 13px; }
.audit-actions { display: flex; gap: 8px; }
/* ---------- 概览（0824）---------- */
.audit-summary { margin-bottom: 14px; padding: 14px 16px; border: 1px solid var(--line); border-radius: 8px; background: var(--white); }
.audit-summary > header { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.audit-summary > header > b { font-size: 13px; }
.audit-summary > header select { min-height: 28px; padding: 0 6px; border: 1px solid var(--line); border-radius: 4px; font-size: 12px; }

/*
  两张榜单一窄一宽（0824 修版式）。
  等宽是错的：左边放人名（两三个字），右边放「P02 产品设计和初始过程开发阶段（非自主设计）」，
  同样宽度下右边必然挤爆——初版就是这么把次数顶到卡片外面去的。
*/
.audit-summary-body { display: grid; grid-template-columns: minmax(190px, 1fr) minmax(300px, 1.9fr); gap: 12px; }
.audit-card { min-width: 0; padding: 11px 13px; border: 1px solid var(--line); border-radius: 6px; background: var(--paper, #f7f9fc); font-size: 12px; }
.audit-card > b { display: block; font-size: 12px; }
.audit-card-hint { margin: 2px 0 0; color: var(--muted); font-size: 11px; }
.audit-card-empty { margin: 8px 0 0; color: var(--muted); }
.audit-card ul { display: grid; gap: 1px; margin: 8px 0 0; padding: 0; list-style: none; }

/*
  一行 = 名字 + 右对齐的次数。
  **用 grid 的 `1fr auto` 而不是 flex 的 space-between**：
  space-between 只是"把剩余空间摊开"，名字一长就没有剩余空间，
  次数被直接顶出容器（Tony 截图里那条红框就是这个）。
  `1fr auto` 是"名字吃掉所有可用宽度、次数按内容占位"，名字再长也挤不动次数。
  `min-width: 0` 一个都不能少：grid/flex 子项默认 min-width:auto，
  不写这行，长按钮照样把列撑破。
*/
.audit-card li { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 10px; padding: 3px 4px; border-radius: 4px; }
.audit-card li:hover { background: var(--white); }
/*
  次数右对齐 + 等宽数字：一列数字左右横跳比不对齐更难读。
  tabular-nums 让 8 和 18 占同样宽度，视线能顺着一条直线往下扫。
*/
.audit-card li > span { color: var(--muted); font-size: 11px; white-space: nowrap; font-variant-numeric: tabular-nums; }

/*
  榜单里的名字改成"轻量可点"：底色块 + hover 变蓝，不再用全局那个描边按钮。
  一屏堆着十几个蓝框是纯视觉噪音（截图里最刺眼的就是这个）。
  但**仍然看得出能点**——有底色、有 hover、光标是手型，
  不是一段裸下划线文字（那条老规矩针对的是"看不出能点"，不是"必须描边"）。
*/
/*
  `justify-self: start` 让底色块按**文字长度**收，不要横着铺满整格——
  铺满的话「肖娟」两个字后面拖一大片空色块，比没有底色还难看。
  同时 `max-width: 100%` + ellipsis 兜住那些长阶段名。
*/
.audit-rank { display: block; justify-self: start; max-width: 100%; min-width: 0; padding: 3px 7px; border: 1px solid transparent; border-radius: 4px; background: rgba(0, 117, 174, .07); color: var(--ink); font: inherit; text-align: left; cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.audit-rank:hover, .audit-rank:focus-visible { border-color: var(--blue); background: rgba(0, 117, 174, .14); color: var(--blue); }

/* 每天多少条：一排小柱子，最高的那天占满 */
.audit-daily { grid-column: 1 / -1; }
.audit-bars { display: flex; align-items: flex-end; gap: 4px; height: 76px; margin: 10px 0 0; padding: 0; list-style: none; overflow-x: auto; }
.audit-bars li { display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 4px; flex: 1 0 20px; height: 100%; }
.audit-bars i { display: block; width: 100%; min-height: 3px; border-radius: 3px 3px 0 0; background: var(--blue); opacity: .82; transition: opacity .12s; }
.audit-bars li:hover i { opacity: 1; }
.audit-bars em { color: var(--muted); font-style: normal; font-size: 10px; white-space: nowrap; }

@media (max-width: 900px) {
  /* 窄屏两张榜单叠起来，宽的那张不再强撑 300px */
  .audit-summary-body { grid-template-columns: 1fr; }
}

/*
  从概览点进来时的结果区抬头（0825）。用蓝色左边框而不是又一个灰框：
  它要在一屏里第一眼被看到，否则又回到「点了像没反应」。
  `scroll-margin-top` 是为了滚过来时不被顶到贴边，留一点呼吸。
*/
.audit-active-filter { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 0 0 10px; padding: 9px 12px; border-left: 3px solid var(--blue); border-radius: 0 6px 6px 0; background: rgba(0, 117, 174, .07); font-size: 12px; scroll-margin-top: 12px; }
.audit-active-filter > b { color: var(--ink); }
.audit-active-filter > span { color: var(--muted); }

.audit-count { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin: 0 0 10px; color: var(--muted); font-size: 12px; }
.audit-count p { margin: 0; }
.audit-export { display: flex; align-items: center; gap: 8px; }

/*
  列表容器收住 overflow-x（1016）：改前/改后长文案不再把整页撑出横向滚动。
  统计区 / 柱图保留原宽屏布局，不在这里收。
*/
.audit-list {
  display: grid;
  gap: 10px;
  min-width: 0;
  overflow-x: auto;
}

.audit-list article {
  min-width: 0;
  overflow-x: hidden;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-left: 3px solid var(--blue);
  background: var(--white);
}

.audit-list header { display: flex; gap: 8px; flex-wrap: wrap; align-items: baseline; margin-bottom: 8px; }
.audit-list header b { color: var(--blue); font-size: 13px; }
.audit-entity { color: var(--muted); font-size: 12px; }
.audit-subject {
  min-width: 0;
  max-width: 100%;
  font-size: 13px;
  font-weight: 700;
  overflow-wrap: anywhere;
  word-break: break-word;
}
/* 所属项目：当归属标签用，比对象名稍弱但要看得见 */
.audit-project { padding: 1px 7px; border-radius: 3px; background: rgba(0, 117, 174, .1); color: var(--blue); font-size: 12px; font-weight: 600; }
.audit-meta { margin-left: auto; color: var(--muted); font-size: 12px; }
/* 一次操作改了多条：组头略压暗、成员缩进，一眼看出它们是一起发生的（1006） */
.audit-group { border-left: 3px solid var(--blue); background: #f8fafc; }
.audit-group.is-open { margin-bottom: 0; border-bottom-left-radius: 0; border-bottom-right-radius: 0; }
.audit-group-toggle { margin-left: 8px; }
.audit-group-peek { margin: 0; padding-left: 18px; color: var(--muted); font-size: 12px; line-height: 1.7; }
.audit-group-more { list-style: none; color: #9ca3af; }
.audit-list article.audit-group-member { margin-left: 18px; border-left: 3px solid rgba(0, 117, 174, .25); }

/*
  改前 / 改后紧凑对照（1016）。
  宽屏：左右并排（minmax(0,1fr) 吃掉可用宽，长串换行/截断）。
  窄屏：上下叠，箭头隐藏——不要整页横向滚动。
  悬停 title = 全文；超过 4 行用 line-clamp 收住高度。
*/
.audit-changes {
  display: grid;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
  min-width: 0;
}
.audit-changes > li {
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid var(--line);
  border-radius: 4px;
  background: var(--paper, #f7f9fc);
}
.audit-change-field {
  display: block;
  margin-bottom: 4px;
  color: var(--muted);
  font-size: 11px;
  font-weight: 600;
}
.audit-change-pair {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 6px;
  align-items: start;
  min-width: 0;
}
.audit-change-arrow {
  color: var(--muted);
  font-size: 12px;
  line-height: 1.5;
  padding-top: 14px;
}
.audit-change-cell {
  min-width: 0;
  display: grid;
  gap: 2px;
}
.audit-change-cell > em {
  color: var(--muted);
  font-style: normal;
  font-size: 10px;
  font-weight: 600;
}
.audit-before,
.audit-after {
  min-width: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.4;
}
.audit-before { color: var(--muted); text-decoration: line-through; }
.audit-after { color: var(--ink); font-weight: 700; }
/* 没收录的系统内部字段整行压暗：看得见（审计要求不能藏），但一眼知道不用管 */
.audit-changes > li.internal {
  opacity: .72;
}
.audit-changes > li.internal .audit-after {
  font-weight: 400;
  color: var(--muted);
}
@media (max-width: 640px) {
  .audit-change-pair { grid-template-columns: 1fr; }
  .audit-change-arrow { display: none; }
}

.audit-nochange { margin: 0; color: var(--muted); font-size: 12px; }
.audit-module { display: block; margin-top: 6px; color: var(--muted); font-size: 12px; }
.audit-pager { display: flex; gap: 8px; margin-top: 12px; }
</style>
